import { useEffect, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Alert, MenuItem, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'

import DateTimePickerInput from '@/components/ui/inputs/DateTimePickerInput'
import { useCorrectTimeTrackingSessionMutation } from '@/core/api'
import { TimeSession } from '@/core/api/time-tracking'

import { REASON_CODE_VALUES, getReasonCodeOptions } from '../lib/reasonCodes'

const schema = yup.object({
  startTime: yup.mixed<dayjs.Dayjs>().required(),
  endTime: yup.mixed<dayjs.Dayjs>().nullable().defined(),
  reasonCode: yup.string().oneOf(REASON_CODE_VALUES).optional(),
  comment: yup.string().optional(),
})

type FormData = yup.InferType<typeof schema>

type CorrectSessionDialogProps = {
  open: boolean
  session: TimeSession | null
  isAdminView?: boolean
  onClose: () => void
}

export function CorrectSessionDialog({ open, session, isAdminView = false, onClose }: CorrectSessionDialogProps) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const reasonCodeOptions = getReasonCodeOptions()

  const [correctSession, { isLoading }] = useCorrectTimeTrackingSessionMutation()
  const [apiError, setApiError] = useState<string | null>(null)

  const { control, handleSubmit, register, reset } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
  })

  useEffect(() => {
    if (session) {
      reset({
        startTime: dayjs(session.startTime),
        endTime: session.endTime ? dayjs(session.endTime) : null,
      })
      setApiError(null)
    }
  }, [session, reset])

  const onSubmit = async (data: FormData) => {
    if (!session) return
    if (isAdminView && !data.reasonCode) return

    setApiError(null)
    try {
      await correctSession({
        sessionId: session._id,
        startTime: data.startTime!.toISOString(),
        ...(data.endTime && { endTime: data.endTime.toISOString() }),
        ...(isAdminView && { reasonCode: data.reasonCode as any, comment: data.comment }),
      }).unwrap()

      enqueueSnackbar(t`Session corrected successfully`, { variant: 'success' })
      onClose()
    } catch (err: any) {
      const message = err?.data?.message || err?.message || t`Failed to correct session`
      setApiError(typeof message === 'string' ? message : t`Failed to correct session`)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Trans>Correct session</Trans>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => <DateTimePickerInput label={t`Start time`} value={field.value ?? null} onChange={(val) => field.onChange(val)} fullWidth />}
            />
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => <DateTimePickerInput label={t`End time`} value={field.value ?? null} onChange={(val) => field.onChange(val)} fullWidth />}
            />
            {isAdminView && (
              <>
                <Controller
                  name="reasonCode"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select label={t`Reason`} fullWidth required>
                      {reasonCodeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <TextField {...register('comment')} label={t`Comment`} multiline rows={2} fullWidth />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            <Trans>Save</Trans>
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
